/**
 * UserAuthContext.jsx
 * src/context/UserAuthContext.jsx
 *
 * Provides login state + helpers to the entire user-facing app.
 *
 * Wrap your root in <UserAuthProvider>:
 *   import { UserAuthProvider } from './context/UserAuthContext';
 *   <UserAuthProvider><App /></UserAuthProvider>
 *
 * Consume anywhere:
 *   import { useUserAuth } from './context/UserAuthContext';
 *   const { user, isLoggedIn, login, logout } = useUserAuth();
 */

import { createContext, useContext, useState, useEffect } from "react";

const UserAuthContext = createContext(null);

export function UserAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true); // true while reading localStorage

  /* Restore session on mount */
  useEffect(() => {
    try {
      const stored = localStorage.getItem("dental_user");
      if (stored) {
        const parsed = JSON.parse(stored);
        setUser(parsed);
        setIsLoggedIn(true);
      }
    } catch {}
    setLoading(false);
  }, []);

  /**
   * Call after a successful login API response.
   * userData: { user_id, name, email, mobile, telegram_chat_id?, ... }
   */
  const login = (userData) => {
    const data = {
      user_id: userData.patient_id || userData.id,
      name: userData.name || userData.Name,
      email: userData.email || userData.Email,
    };
    localStorage.setItem("dental_user", JSON.stringify(data));
    setUser(data);
    setIsLoggedIn(true);
  };

  const logout = () => {
    localStorage.removeItem("dental_user");
    setUser(null);
    setIsLoggedIn(false);
  };

  return (
    <UserAuthContext.Provider
      value={{ user, isLoggedIn, loading, login, logout }}
    >
      {children}
    </UserAuthContext.Provider>
  );
}

export function useUserAuth() {
  const ctx = useContext(UserAuthContext);
  if (!ctx)
    throw new Error("useUserAuth must be used inside <UserAuthProvider>");
  return ctx;
}
